import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useNotification } from '@/components/notifications';
import { liveTranscriptionQueryKey } from '@/hooks/queries/use-live-transcription-state';
import { stopLiveTranscription } from '@/services/workflow/live-transcription';

interface UseStopLiveTranscriptionOptions {
  orgId?: string;
  workflowId?: string;
  sessionId?: string;
  nodeId: string;
}

export function useStopLiveTranscription({
  orgId,
  workflowId,
  sessionId,
  nodeId,
}: UseStopLiveTranscriptionOptions) {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const queryKey = liveTranscriptionQueryKey(
    orgId,
    workflowId,
    sessionId,
    nodeId,
  );

  return useMutation({
    mutationFn: () => {
      if (!orgId || !workflowId || !sessionId) {
        throw new Error('Missing live transcription session information');
      }
      return stopLiveTranscription(orgId, workflowId, sessionId, nodeId);
    },
    onSuccess: (result) => {
      queryClient.setQueryData(queryKey, (previous: unknown) => ({
        ...(typeof previous === 'object' && previous ? previous : {}),
        status: result.status,
      }));
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        extra: {
          context: 'stop-live-transcription',
          workflowId,
          sessionId,
          nodeId,
        },
      });
      notification.error(
        error instanceof Error
          ? error.message
          : 'Failed to stop live transcription',
      );
    },
  });
}
